import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

type FlashType = 'success' | 'error' | 'info';

interface FlashContextType {
  flash: { message: string; type: FlashType } | null;
  setFlash: (flash: { message: string; type: FlashType } | null) => void;
}

const FlashContext = createContext<FlashContextType | undefined>(undefined);

export const FlashProvider = ({ children }: { children: ReactNode }) => {
  const [flash, setFlash] = useState<{ message: string; type: FlashType } | null>(null);

  useEffect(() => {
    // Check localStorage only
    const localFlash = localStorage.getItem('flash');
    if (localFlash) {
      try {
        setFlash(JSON.parse(localFlash));
        localStorage.removeItem('flash'); // Clear after reading
      } catch (e) {
        console.error('Error parsing localStorage flash:', e);
        localStorage.removeItem('flash'); // Clear even if parsing fails
      }
    }
  }, []);

  const setFlashWithStorage = (flash: { message: string; type: FlashType } | null) => {
    if (flash) {
      // Set flash in localStorage to persist across soft navigations within the SPA
      // It will be cleared on the next component mount that reads it (usually App layout)
      localStorage.setItem('flash', JSON.stringify(flash));
    } else {
      // If setting flash to null, ensure localStorage is also cleared
      localStorage.removeItem('flash');
    }
    setFlash(flash); // Update the state for immediate display
  };

  return (
    <FlashContext.Provider value={{ flash, setFlash: setFlashWithStorage }}>
      {children}
    </FlashContext.Provider>
  );
};

export const useFlash = () => {
  const context = useContext(FlashContext);
  if (context === undefined) {
    throw new Error('useFlash must be used within a FlashProvider');
  }
  return context;
};
